import { useState } from "react";
import { Text, Collapse, ActionIcon } from "@mantine/core";
import { Card, Badge, Table } from "@xplatform/design-system";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import type { EvaluationDetailViewModel } from "@models/evaluation";
import { formatAccuracy, formatDuration, getAccuracyColor } from "@utils/evaluationFormatters";
import "./EvaluationDetailCard.scss";

interface EvaluationDetailCardProps {
    detail: EvaluationDetailViewModel;
}

export const EvaluationDetailCard = ({ detail }: EvaluationDetailCardProps) => {
    const [expanded, setExpanded] = useState(false);

    const hasDifferences = detail.differences && detail.differences.length > 0;

    return (
        <Card className="evaluation-detail-card" withBorder>
            <div className="evaluation-detail-card__content">
                <div className="evaluation-detail-card__header">
                    <div className="evaluation-detail-card__file-info">
                        <Text size="sm" fw={600}>
                            {detail.fileName}
                        </Text>
                        <Text size="xs" c="dimmed">
                            {detail.doctype}
                        </Text>
                    </div>
                    <div className="evaluation-detail-card__metrics">
                        <Badge color={getAccuracyColor(detail.accuracy)} size="sm">
                            {formatAccuracy(detail.accuracy)}
                        </Badge>
                        <Text size="xs" c="dimmed">
                            {formatDuration(detail.duration)}
                        </Text>
                    </div>
                </div>

                <div className="evaluation-detail-card__stats">
                    <div className="evaluation-detail-card__stat">
                        <Text size="xs" c="dimmed">
                            Precision
                        </Text>
                        <Text size="sm">{formatAccuracy(detail.precision)}</Text>
                    </div>
                    <div className="evaluation-detail-card__stat">
                        <Text size="xs" c="dimmed">
                            Recall
                        </Text>
                        <Text size="sm">{formatAccuracy(detail.recall)}</Text>
                    </div>
                    <div className="evaluation-detail-card__stat">
                        <Text size="xs" c="dimmed">
                            F1 Score
                        </Text>
                        <Text size="sm">{formatAccuracy(detail.f1Score)}</Text>
                    </div>
                </div>

                <div className="evaluation-detail-card__confusion">
                    <div className="evaluation-detail-card__confusion-item">
                        <Text size="xs" c="dimmed">
                            TP
                        </Text>
                        <Badge size="sm" color="green">
                            {detail.truePositives}
                        </Badge>
                    </div>
                    <div className="evaluation-detail-card__confusion-item">
                        <Text size="xs" c="dimmed">
                            FP
                        </Text>
                        <Badge size="sm" color="red">
                            {detail.falsePositives}
                        </Badge>
                    </div>
                    <div className="evaluation-detail-card__confusion-item">
                        <Text size="xs" c="dimmed">
                            FN
                        </Text>
                        <Badge size="sm" color="yellow">
                            {detail.falseNegatives}
                        </Badge>
                    </div>
                </div>

                {hasDifferences && (
                    <>
                        <div
                            className="evaluation-detail-card__differences-header"
                            onClick={() => setExpanded(!expanded)}
                        >
                            <Text size="sm" fw={600}>
                                Differences ({detail.differences.length})
                            </Text>
                            <ActionIcon size="sm" variant="subtle" component="span">
                                {expanded ? (
                                    <IconChevronUp size={16} />
                                ) : (
                                    <IconChevronDown size={16} />
                                )}
                            </ActionIcon>
                        </div>

                        <Collapse in={expanded}>
                            <div className="evaluation-detail-card__differences-content">
                                <Table>
                                    <Table.Tbody>
                                        {detail.differences.map((diff, index) => (
                                            <Table.Tr key={index}>
                                                <Table.Td>
                                                    <Text size="sm" fw={500}>
                                                        {diff.field}
                                                    </Text>
                                                    <Text size="xs" c="dimmed">
                                                        {diff.diffType}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="xs" c="dimmed">
                                                        Expected
                                                    </Text>
                                                    <Text size="sm">{JSON.stringify(diff.expected)}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="xs" c="dimmed">
                                                        Actual
                                                    </Text>
                                                    <Text size="sm">{JSON.stringify(diff.actual)}</Text>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </div>
                        </Collapse>
                    </>
                )}
            </div>
        </Card>
    );
};
